import { Injectable } from '@angular/core';

import { BehaviorSubject, Observable } from 'rxjs';

import { FilterTypeModel } from '../models/abstract-filter-type.model';
import { FilterGroupModel } from '../models/filter-group.model';
import { FilterTypeCheckboxModel } from '../../../Common/Filter/models/filter-type-checkbox.model';
import { FilterOptionModel } from '../../../Common/Filter/models/filter-option.model';
import { FacetValueModel } from '../../../api/mentions/model/facet-value.model';
import { JournodexSourceModel } from '../../../Common/Journodex/models/journodex-source.model';
import { SearchCriteriaSourcesEnum } from '../../../api/mentions/enum/search-criteria-sources.enum';
import { SentimentModel } from '../../../Monitor/Shared/models/sentiment.model';
import { LanguageModel } from '../../../Core/Localization/models/language.model';
import { isoLanguageNames } from '../../../Common/Forms/data/iso-language-names';
import { CheckboxGroup, ProminenceFilterModel } from '../../../Monitor/Shared/components/ProminenceFilter/prominence-filter.model';
import { ProminenceFacetModel } from '../../../api/mentions/model/prominence-facet.model';
import { ProminenceLevel, ProminenceScoreModel } from '../../../Monitor/Shared/models/prominence-score.model';
import { mapSentimentValueToEnum, SearchCriteriaQuerySentimentsEnum } from '../../../api/mentions/enum/search-criteria-query-sentiments.enum';
import { SavedFilter } from '../models/saved-filter.model';
import { SavedFilterCard, SavedFilterTypeModel } from '../components/SavedFilterWrapper/saved-filter-type.model';
import { SavedFilterWrapperComponent } from '../components/SavedFilterWrapper/saved-filter-wrapper.component';
import { monitoringFiltersEnum } from '../../../Monitor/Shared/enums/monitoring-filters.enum';

@Injectable()
export abstract class FilterService {

    protected filters: FilterTypeModel[];
    protected filters$: BehaviorSubject<FilterTypeModel[]>;

    protected flatFilters: FilterTypeModel[];
    protected flatFilters$: BehaviorSubject<FilterTypeModel[]>;

    protected readonly savedFiltersType = new SavedFilterTypeModel(
        monitoringFiltersEnum.savedFilters,
        'Saved Filters',
        SavedFilterWrapperComponent
    );

    constructor() {
        this.filters = [];
        this.filters$ = new BehaviorSubject<FilterTypeModel[]>(this.filters);
        this.flatFilters = [];
        this.flatFilters$ = new BehaviorSubject<FilterTypeModel[]>(this.flatFilters);
    }

    /**
     * Sets the filters
     * @param filters A list of filters
     */
    protected setFilters(filters: FilterTypeModel[]): void {
        this.filters = filters;
        this.filters$.next(this.filters);

        this.flatFilters = filters.reduce<FilterTypeModel[]>((flatFilters, filter) => {
            if (filter instanceof FilterGroupModel) {
                flatFilters.push(...filter.children);
            } else {
                flatFilters.push(filter);
            }
            return flatFilters;
        }, []);
        this.flatFilters$.next(this.flatFilters);
    }

    /**
     * Gets the filters
     */
    getFilters(): Observable<FilterTypeModel[]> {
        return this.filters$;
    }

    /**
     * Gets the filters
     */
    getFlatFilters(): Observable<FilterTypeModel[]> {
        return this.flatFilters$;
    }

    /**
     * Saves the current filters as the actual filters
     */
    applyFilters(): void {
        this.flatFilters.forEach((filter) => {
            filter.setValue(filter.getCurrentValue());
            filter.setIncludeExclude(filter.currentIncludeExclude);
        });

        this.setFilters(this.filters);
    }

    /**
     * Clears the current set of filters, and broadcasts the update
     */
    clearFilters(): void {
        this.flatFilters.forEach(filter => filter.clear());
        this.setFilters(this.filters);
    }


    /**
     * Populates the options for the authors filter
     * @param facets A list of facets from search results
     */
    protected getAuthorsOptions(authorsFilter: FilterTypeCheckboxModel, facets: FacetValueModel[]) {
        const oldOptions = authorsFilter.options;
        authorsFilter.options = facets
            .filter(val => ('' + val.value).trim().length > 0)
            .map((val) => {
                const src = new JournodexSourceModel();
                src.entityType = SearchCriteriaSourcesEnum.journalist;
                src.entityName = val.value;

                return {
                    label: val.value,
                    value: src,
                    count: val.count
                };
            });
        this.keepSelectedCheckboxOptions<JournodexSourceModel>(
            authorsFilter,
            oldOptions,
            (a, b) => b.entityName === a.entityName
        );
        authorsFilter.loading = false;
    }

    /**
     * Populates the options for the sentiment filter
     * @param facets A list of facets from search results
     */
    protected getSentimentsOptions(
        sentimentFilter: FilterTypeCheckboxModel<SentimentModel>,
        facets: FacetValueModel[],
        selectedSentiments: SentimentModel[] = []
    ) {
        const oldOptions = sentimentFilter.options;
        const sentimentTotals = {};

        if (selectedSentiments.length === 0) {
            // if none are selected, treat as "all"
            selectedSentiments = [
                new SentimentModel(SearchCriteriaQuerySentimentsEnum.positive),
                new SentimentModel(SearchCriteriaQuerySentimentsEnum.neutral),
                new SentimentModel(SearchCriteriaQuerySentimentsEnum.negative),
                new SentimentModel(SearchCriteriaQuerySentimentsEnum.notAvailable)
            ];
        }

        sentimentTotals[SearchCriteriaQuerySentimentsEnum.positive] = 0;
        sentimentTotals[SearchCriteriaQuerySentimentsEnum.neutral] = 0;
        sentimentTotals[SearchCriteriaQuerySentimentsEnum.negative] = 0;
        sentimentTotals[SearchCriteriaQuerySentimentsEnum.notAvailable] = 0;

        for (const facetValue of facets) {
            sentimentTotals[mapSentimentValueToEnum(facetValue.value)] += facetValue.count;
        }

        const options: FilterOptionModel<SentimentModel>[] = [];

        for (const key in sentimentTotals) {
            if (selectedSentiments.find(sentiment => sentiment.id === key)) {
                // if in search criteria
                options.push({
                    label: `Sentiment.${key}`,
                    value: new SentimentModel(key as SearchCriteriaQuerySentimentsEnum),
                    count: sentimentTotals[key]
                });
            }
        }
        sentimentFilter.options = options;
        this.keepSelectedCheckboxOptions<SentimentModel>(
            sentimentFilter,
            oldOptions,
            (a, b) => b.id === a.id
        );
        sentimentFilter.loading = false;
    }

    /**
     * Populates the options for the language filter
     * @param facets A list of facets from search results
     */
    protected getLanguageOptions(languageFilter: FilterTypeCheckboxModel<LanguageModel>, facets: FacetValueModel[]) {
        const oldOptions = languageFilter.options;
        languageFilter.options = facets
            .filter((facet) => facet.value in isoLanguageNames)
            .map((facet) => {
                return {
                    label: isoLanguageNames[facet.value],
                    value: new LanguageModel(facet.value),
                    count: facet.count
                };
            });
        this.keepSelectedCheckboxOptions<LanguageModel>(
            languageFilter,
            oldOptions,
            (a, b) => b.id === a.id
        );
        languageFilter.loading = false;
    }

    protected getHashtagOptions(hashtagFilters: FilterTypeCheckboxModel<string>, facets: FacetValueModel[]) {
        const oldOptions = hashtagFilters.options;
        hashtagFilters.options = facets
            .map((facet) => {
                return {
                    label: facet.value,
                    value: facet.value as any,
                    count: facet.count
                };
            });

        this.keepSelectedCheckboxOptions<string>(
            hashtagFilters,
            oldOptions,
            (a, b) => b === a
        );

        hashtagFilters.loading = false;
    }

    protected getProminenceOptions(prominenceFilter: ProminenceFilterModel, prominenceFacets: ProminenceFacetModel[]) {
        if (!prominenceFacets) {
            prominenceFilter.loading = false;
            prominenceFilter.options = [];
            return;
        }
        const oldOptions = prominenceFilter.options;

        const prominenceGroups: CheckboxGroup[] = [];
        for (const facet of prominenceFacets) {
            const checkboxGroup = {
                label: facet.prominenceGroup.name,
                id : facet.prominenceGroup.id,
                checkboxes: [
                    { label : 'Mention.details.prominenceLevel.veryHigh', value: ProminenceLevel.VERY_HIGH, count: 0},
                    { label : 'Mention.details.prominenceLevel.high', value: ProminenceLevel.HIGH, count: 0},
                    { label : 'Mention.details.prominenceLevel.moderate', value: ProminenceLevel.MODERATE, count: 0},
                    { label : 'Mention.details.prominenceLevel.low', value: ProminenceLevel.LOW, count: 0},
                    { label : 'Mention.details.prominenceLevel.veryLow', value: ProminenceLevel.VERY_LOW, count: 0},
                    { label : 'Mention.details.prominenceLevel.none', value: ProminenceLevel.NONE, count: 0},
                ]
            };
            prominenceGroups.push(checkboxGroup);

            for (const count of facet.ranges) {
                let level = ProminenceScoreModel.getProminenceLevel(count.end);

                if (count.start === 0) {
                    level = ProminenceLevel.NONE;
                }
                checkboxGroup.checkboxes.find(x => x.value === level).count += count.count;
            }
        }

        prominenceFilter.options = prominenceGroups;

        // TODO ?
        /* this.keepSelectedCheckboxOptions<string>(
            prominenceFilter,
            oldOptions,
            (a, b) => b === a
        ); */

        prominenceFilter.loading = false;
    }



    /**
     * Clears all of the options that are not locked
     * @param filter A checkbox search filter definition
     */
    protected clearUnselectedCheckboxOptions(filter: FilterTypeCheckboxModel<any>, keepSelected: boolean = true): void {
        const oldOptions: FilterOptionModel<any>[] = filter.options;
        filter.options = [];

        if (keepSelected) {
            this.keepSelectedCheckboxOptions(filter, oldOptions);
        }
    }


    /**
     * Re-adds locked options to checkbox filters after the options list changes
     * @param filter A checkbox filter
     * @param oldOptions Previous options from the filter
     */
    protected keepSelectedCheckboxOptions<T>(
        filter: FilterTypeCheckboxModel<any>,
        oldOptions: FilterOptionModel<any>[],
        comparator: (a: T, b: T) => boolean = (a, b) => a === b
    ) {
        const values = filter.getValue();
        if (!values) {
            return;
        }
        const valuesNotInFilter = values.filter((val, index) => {
            const foundOption = filter.options.find((opt) => comparator(val, opt.value));

            if (foundOption) {
                // Make it truly match the value so things work better later
                values[index] = foundOption.value;
            }

            return !foundOption;
        });
        const oldOptionsToAdd = oldOptions.filter((opt) => {
            const foundValue = valuesNotInFilter.find((val) => comparator(val, opt.value));

            if (foundValue) {
                opt.value = foundValue;
            }

            return !!foundValue;
        });
        oldOptionsToAdd.forEach((opt) => delete opt.count);

        filter.options.push(...oldOptionsToAdd);
        filter.setValue(filter.getValue());
    }

    // #region Saved Filters

    // Override these functions when implementing saved filters so it can work properly

    prepareFilterModelToSave(): SavedFilter {
        return new SavedFilter();
    };

    /**
     * Ungroup saved filter means that its query will be merged to the source filter query
     */
    ungroupSavedFilter(query: SavedFilter): void {}

    constructFilterCardFromValue(val: SavedFilter): SavedFilterCard {
        return;
    }

    updateSavedFilters(savedFilters: SavedFilter[]) {
        // Update Saved Filter options
        this.savedFilters.options = savedFilters?.map(savedFilter => {
            return {
                label: savedFilter.name,
                value: savedFilter
            };
        }) ?? [];
        // Update Saved Filter cards
        const currentValue = this.savedFilters.getCurrentValue() ?? [];
        this.savedFilters.filterCards = currentValue
            .map(value => {
                const savedFilter = this.savedFilters.options.find(option => option.value.id === value.id);
                if (!savedFilter) {
                    return null;
                }
                const filterCard = this.savedFilters.filterCards.find(card => card.value.id === value.id);
                return JSON.stringify(filterCard?.value) === JSON.stringify(savedFilter)
                    ? filterCard
                    : this.constructFilterCardFromValue(savedFilter.value);
            })
            .filter(x => !!x);
    }

    get savedFilters(): SavedFilterTypeModel {
        return this.savedFiltersType;
    }
    //#endregion

}
